import { useMemo } from "react";
import { Search, LayoutList, LayoutGrid } from "lucide-react";
import { Input } from "../../../../components/ui/input";
import { Button } from "../../../../components/ui/button";
import { Separator } from "../../../../components/ui/separator";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../../components/ui/select";
import { useObjectFieldValues } from "../../../../hooks/QueryHooks";

type QuickFilter = {
  key: string;
  label: string;
  values: { label: string; value: string }[];
};

/** Max number of options to render as inline chip buttons; above this use a dropdown */
const CHIP_THRESHOLD = 5;

/** Per-slug quick filter field configurations.
 *  `field` must match the column_name stored in meta_data JSONB (native_ prefix). */
const QUICK_FILTER_CONFIG: Record<string, { key: string; label: string; field: string }[]> = {
  deals: [{ key: "stage", label: "Stage", field: "native_stage" }],
  tasks: [
    { key: "status", label: "Status", field: "native_status" },
    { key: "priority", label: "Priority", field: "native_priority" },
  ],
  tickets: [
    { key: "status", label: "Status", field: "native_status" },
    { key: "priority", label: "Priority", field: "native_priority" },
  ],
};

type Props = {
  objectName: string;
  objectSlug: string;
  workspaceId: string;
  total: number;
  isLoading?: boolean;
  searchQuery: string;
  onSearchChange: (query: string) => void;
  searchPlaceholder?: string;
  viewMode?: "list" | "card";
  onViewModeChange?: (mode: "list" | "card") => void;
  showViewToggle?: boolean;
  filterValues?: Record<string, string>;
  onFilterChange?: (key: string, value: string) => void;
};

/** Hook that fetches server-side distinct values for each configured filter field */
function useQuickFilters(objectSlug: string, workspaceId: string): QuickFilter[] {
  const configs = QUICK_FILTER_CONFIG[objectSlug] || [];

  const field0 = configs[0];
  const field1 = configs[1];

  const { data: values0 } = useObjectFieldValues({
    workspaceId,
    objectType: objectSlug,
    field: field0?.field || "",
    enabled: !!field0,
  });
  const { data: values1 } = useObjectFieldValues({
    workspaceId,
    objectType: objectSlug,
    field: field1?.field || "",
    enabled: !!field1,
  });

  return useMemo(() => {
    const results: QuickFilter[] = [];
    const allValues = [values0, values1];

    configs.forEach((cfg, i) => {
      const vals = allValues[i];
      if (!vals || vals.length === 0) return;
      results.push({
        key: cfg.key,
        label: cfg.label,
        values: vals.map((v: any) => ({ label: v.value, value: v.value })),
      });
    });
    return results;
  }, [configs, values0, values1]);
}

/** Search placeholder per slug */
export function getSearchPlaceholder(slug: string): string {
  switch (slug) {
    case "notes":
      return "Search notes...";
    case "deals":
      return "Search deals by title, stage, owner...";
    case "tasks":
    case "tickets":
      return "Search by subject, status, owner...";
    case "meeting_recordings":
      return "Search recordings...";
    default:
      return "Search...";
  }
}

const ObjectTabToolbar = ({
  objectName,
  objectSlug,
  workspaceId,
  total,
  isLoading,
  searchQuery,
  onSearchChange,
  searchPlaceholder = "Search...",
  viewMode,
  onViewModeChange,
  showViewToggle,
  filterValues = {},
  onFilterChange,
}: Props) => {
  const quickFilters = useQuickFilters(objectSlug, workspaceId);

  return (
    <div className="px-4 pt-4 pb-3 space-y-3">
      <div className="flex items-start justify-between">
        <div>
          <h2 className="text-sm font-semibold text-foreground">{objectName}</h2>
          <p className="text-xs text-muted-foreground mt-0.5">
            {isLoading ? "..." : String(total)} {objectName.toLowerCase()}
          </p>
        </div>
        {showViewToggle && onViewModeChange && (
          <div className="flex items-center gap-0.5 rounded-md border p-0.5">
            <Button
              variant={viewMode === "list" ? "default" : "ghost"}
              size="icon"
              className="h-7 w-7"
              onClick={() => onViewModeChange("list")}
            >
              <LayoutList size={14} />
            </Button>
            <Button
              variant={viewMode === "card" ? "default" : "ghost"}
              size="icon"
              className="h-7 w-7"
              onClick={() => onViewModeChange("card")}
            >
              <LayoutGrid size={14} />
            </Button>
          </div>
        )}
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative flex-1 min-w-[200px]">
          <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={searchQuery}
            onChange={(e) => onSearchChange(e.target.value)}
            placeholder={searchPlaceholder}
            className="pl-8 h-8"
          />
        </div>

        {quickFilters.map((filter) =>
          filter.values.length <= CHIP_THRESHOLD ? (
            /* Inline chip buttons for small option sets */
            <div key={filter.key} className="flex items-center gap-1 rounded-md border border-border-chip p-0.5">
              <Button
                variant={!filterValues[filter.key] ? "default" : "ghost"}
                size="sm"
                className="h-6 px-2.5 text-xs"
                onClick={() => onFilterChange?.(filter.key, "")}
              >
                All
              </Button>
              {filter.values.map((opt) => (
                <Button
                  key={opt.value}
                  variant={filterValues[filter.key] === opt.value ? "default" : "ghost"}
                  size="sm"
                  className="h-6 px-2.5 text-xs"
                  onClick={() => onFilterChange?.(filter.key, opt.value)}
                >
                  {opt.label}
                </Button>
              ))}
            </div>
          ) : (
            /* Dropdown select for large option sets */
            <Select
              key={filter.key}
              value={filterValues[filter.key] || "_all"}
              onValueChange={(v) => onFilterChange?.(filter.key, v === "_all" ? "" : v)}
            >
              <SelectTrigger className="w-auto min-w-[120px] h-8 text-xs gap-1">
                <span className="text-muted-foreground mr-0.5">{filter.label}:</span>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="_all" className="text-xs">All</SelectItem>
                {filter.values.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value} className="text-xs">
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          ),
        )}
      </div>

      <Separator />
    </div>
  );
};

export default ObjectTabToolbar;
